import CKEditor5 from 'components/CkEditor5';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import type { AlertColor } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { routes } from 'utils/constants/routes';
import { translation, User } from 'utils/redux/reducer/authentication-slice';
import { BOOKING_DETAILS_SECTION_FIELDS, SuperAdminBookingDetailsSection } from './SuperAdminBookingDetailsSection';
import {
  PAYMENT_CONFIG_SECTION_FIELDS,
  SuperAdminBookingPaymentConfigSection,
} from './SuperAdminBookingPaymentConfigSection';
import {
  GUEST_BOOKING_SECTION_FIELDS,
  SuperAdminBookingGuestDetailsSection,
  type SaBookingDetailPayload,
} from './SuperAdminBookingGuestDetailsSection';
import { getSaBookingFieldLabel } from './saBookingFieldLabels';
import { SuperAdminBookingLineItemCards } from './SuperAdminBookingLineItemCards';

type SuperAdminBookingDetail = {
  booking: Record<string, unknown>;
  bookingLineItems: Record<string, unknown>[];
};

const HIDDEN_DETAIL_FIELDS = new Set(['id', 'tenantId']);

/** API fields that are booleans (including nullable `Boolean`); null/undefined still shown as switches. */
const BOOLEAN_API_FIELDS = new Set<string>([
  'deleted',
  'company',
  'customerBooking',
  'isGuestBooking',
  'isCorporateBooking',
  'isComplementary',
  'externalBooking',
  'isTdsDeducted',
  'isRoomPayment',
  'overrideHallRental',
  'overridePricePerPerson',
  'overrideMinimumRevenue',
  'overridePricePerHour',
  'hourlyPricing',
]);

function isBooleanApiField(field: string): boolean {
  return BOOLEAN_API_FIELDS.has(field);
}

/** Coerce API boolean-ish values for read-only switches (nullable Booleans, occasional strings). */
function coerceBooleanDisplay(raw: unknown): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw !== 0;
  }
  return Boolean(raw);
}

/** Shown in the audit strip at the top; excluded from accordions. */
const AUDIT_BOOKING_FIELDS = ['bookingReference', 'createdAt', 'createdBy', 'deleted', 'editedAt', 'editedBy'] as const;
const AUDIT_BOOKING_FIELD_SET = new Set<string>(AUDIT_BOOKING_FIELDS);

/** Hidden from accordions only (still in API payload). */
const UI_HIDDEN_BOOKING_FIELDS = new Set([
  'language',
  'organizationId',
  'userId',
  'salesAccountId',
  'salesBranchId',
  'agentId',
  'revenueOwnerUserIds',
]);

const BOOKING_DETAILS_FIELD_SET = new Set<string>(BOOKING_DETAILS_SECTION_FIELDS);
const PAYMENT_CONFIG_FIELD_SET = new Set<string>(PAYMENT_CONFIG_SECTION_FIELDS);

type BookingFieldRow = { id: string; field: string; value: string; raw: unknown };

/** Logical groups for booking payload fields (API camelCase). Order within each group is preserved. */
const BOOKING_FIELD_SECTIONS: { sectionKey: string; titleKey: keyof typeof strings; fields: readonly string[] }[] = [
  {
    sectionKey: 'guest_company',
    titleKey: 'sa_booking_lookup_section_guest_company',
    fields: [...GUEST_BOOKING_SECTION_FIELDS],
  },
];

const DATE_ONLY_FIELDS = new Set(['bookingDate', 'startDate', 'endDate', 'bookingFrom', 'bookingTo']);

const DATETIME_FIELDS = new Set(['createdAt', 'editedAt', 'paymentDate']);

function shouldAttemptTimestampParse(raw: unknown, field: string): boolean {
  if (DATE_ONLY_FIELDS.has(field) || DATETIME_FIELDS.has(field)) return true;
  if (field.endsWith('Date')) return true;
  if (field.endsWith('At')) return true;
  if (field === 'bookingFrom' || field === 'bookingTo') return true;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw.trim())) return true;
  if (
    Array.isArray(raw) &&
    raw.length >= 3 &&
    typeof (raw as number[])[0] === 'number' &&
    (raw as number[])[0] > 1900
  ) {
    return true;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw > 1e12) return true;
    if (raw > 1e9 && raw < 1e12) return true;
  }
  return false;
}

function parseToDayjs(raw: unknown): dayjs.Dayjs | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw > 1e12) {
      const d = dayjs(raw);
      return d.isValid() ? d : null;
    }
    if (raw > 1e9 && raw < 1e12) {
      const d = dayjs(raw * 1000);
      return d.isValid() ? d : null;
    }
    return null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const d = dayjs(trimmed);
    return d.isValid() ? d : null;
  }
  if (Array.isArray(raw) && raw.length >= 3) {
    const y = Number(raw[0]);
    const mo = Number(raw[1]);
    const day = Number(raw[2]);
    const h = raw.length > 3 ? Number(raw[3]) || 0 : 0;
    const mi = raw.length > 4 ? Number(raw[4]) || 0 : 0;
    const s = raw.length > 5 ? Number(raw[5]) || 0 : 0;
    if ([y, mo, day].some(n => Number.isNaN(n))) return null;
    const d = dayjs(new Date(y, mo - 1, day, h, mi, s));
    return d.isValid() ? d : null;
  }
  return null;
}

function formatTimestampForField(raw: unknown, field: string): string | null {
  if (!shouldAttemptTimestampParse(raw, field)) return null;
  const d = parseToDayjs(raw);
  if (!d) return null;
  if (DATE_ONLY_FIELDS.has(field)) {
    return d.format('DD MMM YYYY');
  }
  if (DATETIME_FIELDS.has(field)) {
    return d.format('DD MMM YYYY, HH:mm:ss');
  }
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
    return d.format('DD MMM YYYY');
  }
  if (field.endsWith('At')) {
    return d.format('DD MMM YYYY, HH:mm:ss');
  }
  if (field.endsWith('Date')) {
    return d.format('DD MMM YYYY');
  }
  return d.format('DD MMM YYYY, HH:mm:ss');
}

/** Formats API values for display (timestamps, primitives, JSON fallback). */
function formatForDisplay(raw: unknown, field: string): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  if (typeof raw === 'boolean') {
    return String(raw);
  }
  const asTs = formatTimestampForField(raw, field);
  if (asTs !== null) {
    return asTs;
  }
  if (typeof raw === 'object') {
    return JSON.stringify(raw);
  }
  return String(raw);
}

const WIDE_BOOKING_FIELDS = new Set([
  'address',
  'additionalComments',
  'requestedChanges',
  'cancelReason',
  'paymentOverDueText',
]);

const ReadOnlyBookingField: FC<{ label: string; value: string }> = ({ label, value }) => {
  const display = value || '—';
  const multiline = display.length > 72 || display.includes('\n');
  return (
    <TextField
      label={label}
      value={display}
      fullWidth
      variant="outlined"
      size="small"
      multiline={multiline}
      minRows={multiline ? 2 : 1}
      maxRows={multiline ? 12 : 1}
      InputProps={{ readOnly: true }}
      sx={{
        '& .MuiInputBase-input': {
          cursor: 'default',
        },
        '& .MuiOutlinedInput-root': {
          bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
        },
      }}
    />
  );
};

/** Stored as HTML (same as New / Edit booking); read-only CKEditor to match the rest of the app. */
const ReadOnlyAdditionalCommentsField: FC<{ label: string; html: string; instanceKey: string }> = ({
  label,
  html,
  instanceKey,
}) => (
  <Box
    sx={{
      width: '100%',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
      '& .ck.ck-editor__editable_inline': {
        minHeight: '140px !important',
      },
    }}>
    <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ px: 1, pb: 1 }} key={instanceKey}>
      <CKEditor5 label="" value={html} disabled />
    </Box>
  </Box>
);

const BooleanBookingField: FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 1.25,
      py: 0.75,
      minHeight: 40,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
    }}>
    <Switch checked={checked} disabled size="small" sx={{ ml: -0.75 }} />
    <Typography variant="body2" color="text.primary" component="span" sx={{ lineHeight: 1.35, flex: 1 }}>
      {label}
    </Typography>
  </Box>
);

const DisplayBookingField: FC<{
  field: string;
  label: string;
  raw: unknown;
  value: string;
  /** Remount CKEditor when loading a different booking */
  htmlEditorInstanceKey?: string;
}> = ({ field, label, raw, value, htmlEditorInstanceKey }) => {
  if (isBooleanApiField(field) || typeof raw === 'boolean') {
    return <BooleanBookingField label={label} checked={coerceBooleanDisplay(raw)} />;
  }
  if (field === 'additionalComments') {
    const html = typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
    return (
      <ReadOnlyAdditionalCommentsField
        label={label}
        html={html}
        instanceKey={htmlEditorInstanceKey ?? 'additionalComments'}
      />
    );
  }
  return <ReadOnlyBookingField label={label} value={value} />;
};

const buildColumns = (rows: Record<string, unknown>[]): GridColDef[] => {
  if (!rows.length) {
    return [];
  }
  const keys = Object.keys(rows[0]).filter(k => !HIDDEN_DETAIL_FIELDS.has(k));
  return keys.map(field => {
    const columnHasBoolean =
      isBooleanApiField(field) ||
      rows.some(r => r[field] !== null && r[field] !== undefined && typeof r[field] === 'boolean');
    const col: GridColDef = {
      field,
      headerName: getSaBookingFieldLabel(field),
      flex: 1,
      minWidth: 112,
      valueFormatter: (params: GridValueFormatterParams) => formatForDisplay(params.value, field),
    };
    if (field === 'additionalComments') {
      col.flex = 2;
      col.minWidth = 200;
      col.renderCell = params => {
        const html = params.value == null ? '' : String(params.value);
        if (!html.trim()) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              maxHeight: 120,
              overflow: 'auto',
              py: 0.5,
              width: '100%',
              '& p': { margin: 0 },
            }}>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </Box>
        );
      };
    } else if (columnHasBoolean) {
      col.renderCell = params => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', py: 0.5 }}>
          <Switch checked={coerceBooleanDisplay(params.value)} disabled size="small" />
        </Box>
      );
    }
    return col;
  });
};

const SuperAdminBookingLookup: FC = () => {
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const translationValue = useSelector(translation);
  const location = useLocation();
  const navigate = useNavigate();
  const [orderRef, setOrderRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SuperAdminBookingDetail | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const isSuperAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';

  const htmlEditorInstanceKey = useMemo(() => {
    const ref = data?.booking != null ? (data.booking as Record<string, unknown>)['bookingReference'] : undefined;
    return String(ref ?? orderRef ?? '');
  }, [data?.booking, orderRef]);

  const handleSectionSave = useCallback((sectionKey: string) => {
    if (sectionKey === 'guest_company') {
      return;
    }
    setSnackbar({
      open: true,
      message: strings.sa_booking_lookup_save_not_ready,
      severity: 'info',
    });
  }, []);

  const handleGuestDetailsUpdated = useCallback((detail: SaBookingDetailPayload) => {
    setData(detail as SuperAdminBookingDetail);
    setSnackbar({
      open: true,
      message: strings.sa_booking_lookup_guest_saved,
      severity: 'success',
    });
  }, []);

  const handleBookingDetailsUpdated = useCallback((detail: SaBookingDetailPayload) => {
    setData(detail as SuperAdminBookingDetail);
    setSnackbar({
      open: true,
      message: strings.sa_booking_lookup_booking_details_saved,
      severity: 'success',
    });
  }, []);

  const handlePaymentConfigUpdated = useCallback((detail: SaBookingDetailPayload) => {
    setData(detail as SuperAdminBookingDetail);
    setSnackbar({
      open: true,
      message: strings.sa_booking_lookup_payment_config_saved,
      severity: 'success',
    });
  }, []);

  const handleLineItemDeletedOrRestored = useCallback((detail: SuperAdminBookingDetail, kind: 'delete' | 'restore') => {
    setData(detail);
    setSnackbar({
      open: true,
      message: kind === 'delete' ? strings.sa_booking_lookup_li_deleted_ok : strings.sa_booking_lookup_li_restored_ok,
      severity: 'success',
    });
  }, []);

  const handleLineItemMutationError = useCallback((message: string) => {
    setSnackbar({
      open: true,
      message: message || strings.sa_booking_lookup_li_mutation_error,
      severity: 'error',
    });
  }, []);

  const auditBookingFields = useMemo(() => {
    if (!data?.booking) {
      return [];
    }
    const b = data.booking;
    return AUDIT_BOOKING_FIELDS.map(field => {
      const raw = b[field];
      return { field, raw, value: formatForDisplay(raw, field) };
    });
  }, [data, translationValue]);

  const bookingRows = useMemo((): BookingFieldRow[] => {
    if (!data?.booking) {
      return [];
    }
    const { payments, ...rest } = data.booking as { payments?: unknown[] } & Record<string, unknown>;
    return Object.entries(rest)
      .filter(
        ([field]) =>
          !HIDDEN_DETAIL_FIELDS.has(field) &&
          !AUDIT_BOOKING_FIELD_SET.has(field) &&
          !UI_HIDDEN_BOOKING_FIELDS.has(field) &&
          !BOOKING_DETAILS_FIELD_SET.has(field) &&
          !PAYMENT_CONFIG_FIELD_SET.has(field),
      )
      .map(([field, value]) => ({
        id: field,
        field,
        raw: value,
        value: formatForDisplay(value, field),
      }));
  }, [data]);

  const bookingSections = useMemo(() => {
    const byField = new Map(bookingRows.map(r => [r.field, r]));
    const assigned = new Set<string>();
    const sections: { sectionKey: string; title: string; rows: BookingFieldRow[] }[] = [];

    for (const def of BOOKING_FIELD_SECTIONS) {
      const rows: BookingFieldRow[] = [];
      for (const f of def.fields) {
        const row = byField.get(f);
        if (row) {
          rows.push(row);
          assigned.add(f);
        }
      }
      if (rows.length > 0) {
        sections.push({ sectionKey: def.sectionKey, title: String(strings[def.titleKey]), rows });
      }
    }

    const otherRows = bookingRows.filter(r => !assigned.has(r.field));
    if (otherRows.length > 0) {
      sections.push({
        sectionKey: 'other',
        title: strings.sa_booking_lookup_section_other,
        rows: otherRows.sort((a, b) => a.field.localeCompare(b.field)),
      });
    }

    return sections;
  }, [bookingRows, translationValue]);

  const paymentRows = useMemo(() => {
    const payments = data?.booking?.payments as Record<string, unknown>[] | undefined;
    return payments ?? [];
  }, [data]);

  const paymentColumns = useMemo(() => buildColumns(paymentRows), [paymentRows, translationValue]);

  const onSearch = useCallback(async () => {
    const ref = orderRef.trim();
    if (!ref) {
      setError(strings.sa_booking_lookup_empty_ref);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data: res } = await axiosInstance.get<SuperAdminBookingDetail>(URLS.superAdminBookingByOrderRef(ref));
      setData(res);
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.response?.data?.error_description ?? e.message ?? 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [orderRef]);

  /** Auto-search function for query parameter handling */
  const handleAutoSearch = useCallback(async (searchRef: string) => {
    const ref = searchRef.trim();
    if (!ref) {
      setError(strings.sa_booking_lookup_empty_ref);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data: res } = await axiosInstance.get<SuperAdminBookingDetail>(URLS.superAdminBookingByOrderRef(ref));
      setData(res);
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.response?.data?.error_description ?? e.message ?? 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Handle query parameters for bookingReference and auto-search */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const bookingReference = queryParams.get('bookingReference');
    if (bookingReference) {
      setOrderRef(bookingReference);
      // Auto-search when bookingReference is present in query params
      handleAutoSearch(bookingReference);
      return;
    }

    // Return from line item edit: restore reference field and reload the same booking
    const ref = (location.state as { restoreOrderRef?: string } | null)?.restoreOrderRef?.trim();
    if (!ref) return;
    setOrderRef(ref);
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const { data: res } = await axiosInstance.get<SuperAdminBookingDetail>(URLS.superAdminBookingByOrderRef(ref));
        setData(res);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
    navigate(location.pathname, { replace: true, state: {} });
  }, [handleAutoSearch, location.search, location.state, location.pathname, navigate]);

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom>
          {strings.sa_booking_lookup_title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {strings.sa_booking_lookup_description}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label={strings.sa_booking_lookup_order_ref}
              value={orderRef}
              onChange={e => setOrderRef(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
              onKeyDown={e => e.key === 'Enter' && onSearch()}
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              onClick={onSearch}
              disabled={loading}>
              {strings.sa_booking_lookup_search}
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : theme.palette.grey[50]),
              }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                {strings.sa_booking_lookup_audit_section}
              </Typography>
              <Grid container spacing={2}>
                {auditBookingFields.map(({ field, raw, value }) => (
                  <Grid item xs={12} sm={6} md={4} key={field}>
                    <DisplayBookingField field={field} label={getSaBookingFieldLabel(field)} raw={raw} value={value} />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {strings.sa_booking_lookup_booking_section}
            </Typography>
            <Box sx={{ mb: 3 }}>
              {bookingSections.map((section, index) =>
                section.sectionKey === 'guest_company' ? (
                  <React.Fragment key={section.sectionKey}>
                    <SuperAdminBookingGuestDetailsSection
                      booking={data.booking as Record<string, unknown>}
                      orderReference={htmlEditorInstanceKey}
                      sectionTitle={section.title}
                      rowCount={section.rows.length}
                      defaultExpanded={index === 0}
                      onUpdated={handleGuestDetailsUpdated}
                    />
                    <SuperAdminBookingDetailsSection
                      booking={data.booking as Record<string, unknown>}
                      orderReference={htmlEditorInstanceKey}
                      sectionTitle={String(strings.sa_booking_lookup_section_booking_org_notes)}
                      defaultExpanded={false}
                      htmlEditorInstanceKey={htmlEditorInstanceKey}
                      formatFieldValue={formatForDisplay}
                      onUpdated={handleBookingDetailsUpdated}
                    />
                    <SuperAdminBookingPaymentConfigSection
                      booking={data.booking as Record<string, unknown>}
                      orderReference={htmlEditorInstanceKey}
                      sectionTitle={String(strings.sa_booking_lookup_section_payment_terms)}
                      defaultExpanded={false}
                      formatFieldValue={formatForDisplay}
                      onUpdated={handlePaymentConfigUpdated}
                    />
                  </React.Fragment>
                ) : (
                  <Accordion
                    key={section.sectionKey}
                    defaultExpanded={false}
                    disableGutters
                    sx={{
                      '&:before': { display: 'none' },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      overflow: 'hidden',
                      '&:first-of-type': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                      '&:last-of-type': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 },
                    }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle1" component="div" fontWeight={600}>
                        {section.title}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({section.rows.length})
                        </Typography>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, pt: 2.5 }}>
                      <Grid container spacing={2}>
                        {section.rows.map(row => (
                          <Grid
                            item
                            xs={12}
                            md={WIDE_BOOKING_FIELDS.has(row.field) ? 12 : 6}
                            lg={WIDE_BOOKING_FIELDS.has(row.field) ? 12 : 4}
                            key={`${htmlEditorInstanceKey}-${row.id}`}>
                            <DisplayBookingField
                              field={row.field}
                              label={getSaBookingFieldLabel(row.field)}
                              raw={row.raw}
                              value={row.value}
                              htmlEditorInstanceKey={htmlEditorInstanceKey}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          pt: 2,
                          mt: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSectionSave(section.sectionKey)}>
                          {strings.sa_booking_lookup_save}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ),
              )}
            </Box>

            {paymentRows.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  {strings.sa_booking_lookup_payments_section}
                </Typography>
                <Box sx={{ height: 360, width: '100%', mb: 3 }}>
                  <DataGrid
                    rows={paymentRows.map((r, i) => ({ ...r, id: (r.id as number | string) ?? `p-${i}` }))}
                    columns={paymentColumns}
                    disableSelectionOnClick
                    density="compact"
                    sx={{
                      '.MuiDataGrid-columnHeaders': { fontWeight: 600 },
                    }}
                  />
                </Box>
              </>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {strings.sa_booking_lookup_line_items_section}
            </Typography>
            <SuperAdminBookingLineItemCards
              items={(data.bookingLineItems ?? []) as Record<string, unknown>[]}
              formatFieldValue={formatForDisplay}
              orderReference={String(data.booking?.bookingReference ?? '')}
              onLineItemDeletedOrRestored={handleLineItemDeletedOrRestored}
              onLineItemMutationError={handleLineItemMutationError}
            />
          </>
        )}
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SuperAdminBookingLookup;
